import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';

const DEFAULTS = {
  // Recordatorio a los asignados (grupo "tickets")
  tickets_reminder_enabled: 'false',
  tickets_reminder_frequency_days: '1',
  tickets_reminder_hour: '9',
  tickets_reminder_statuses: 'abierto,en_curso,esperando_cliente',
  // Resumen diario por email (grupo "digest")
  digest_enabled: 'false',
  digest_hour: '8',
  digest_weekdays: '1,2,3,4,5',
  digest_include_tickets: 'true',
  digest_ticket_statuses: 'abierto,en_curso,esperando_cliente,resuelto',
  digest_ticket_priorities: 'urgente,alta,media,baja',
  digest_ticket_min_age_days: '0',
  digest_include_invoices: 'true',
  digest_invoice_collection_statuses: 'pendiente,parcial',
  digest_invoice_include_cancelled: 'false',
  digest_invoice_min_amount: '0',
  digest_invoice_due_filter: 'all',
  digest_invoice_due_days: '0',
  digest_send_to_assignees: 'true',
  digest_extra_recipients: '',
  digest_fallback_to_sender_when_invoices_only: 'true',
};

const WEEKDAYS = [['1', 'Lun'], ['2', 'Mar'], ['3', 'Mié'], ['4', 'Jue'], ['5', 'Vie'], ['6', 'Sáb'], ['0', 'Dom']];
const TICKET_STATUSES = [['abierto', 'Abierto'], ['en_curso', 'En curso'], ['esperando_cliente', 'Esperando cliente'], ['resuelto', 'Resuelto'], ['cerrado', 'Cerrado']];
const TICKET_PRIORITIES = [['urgente', 'Urgente'], ['alta', 'Alta'], ['media', 'Media'], ['baja', 'Baja']];
const INVOICE_COLLECTION_STATUSES = [['pendiente', 'Pendiente'], ['parcial', 'Parcial'], ['cobrada', 'Cobrada']];

const subtitleStyle = { fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginTop: 16, marginBottom: 8 };
const checkboxLabelStyle = { display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '0.85rem' };

function csvToArray(value) {
  return String(value || '').split(',').map(s => s.trim()).filter(Boolean);
}

const TicketsAvisos = () => {
  const { user } = useAuth();
  const modal = useModal();
  const isSuperAdmin = !!user?.isSuperAdmin;

  const [loading, setLoading] = useState(true);
  const [savingReminder, setSavingReminder] = useState(false);
  const [savingDigest, setSavingDigest] = useState(false);
  const [form, setForm] = useState({ ...DEFAULTS });

  useEffect(() => {
    const load = async () => {
      // La configuración de avisos es de empresa: solo el superadmin la lee y edita.
      if (!isSuperAdmin) { setLoading(false); return; }
      try {
        const res = await api.get('/settings');
        const list = Array.isArray(res.data) ? res.data : [];
        const get = (key, def) => list.find(s => s.key === key)?.value ?? def;
        setForm({
          tickets_reminder_enabled: get('tickets_reminder_enabled', DEFAULTS.tickets_reminder_enabled),
          tickets_reminder_frequency_days: get('tickets_reminder_frequency_days', DEFAULTS.tickets_reminder_frequency_days),
          tickets_reminder_hour: get('tickets_reminder_hour', DEFAULTS.tickets_reminder_hour),
          tickets_reminder_statuses: get('tickets_reminder_statuses', DEFAULTS.tickets_reminder_statuses),
          // Compatibilidad con las claves antiguas tickets_daily_digest_*
          digest_enabled: get('digest_enabled', get('tickets_daily_digest_enabled', DEFAULTS.digest_enabled)),
          digest_hour: get('digest_hour', get('tickets_daily_digest_hour', DEFAULTS.digest_hour)),
          digest_weekdays: get('digest_weekdays', DEFAULTS.digest_weekdays),
          digest_include_tickets: get('digest_include_tickets', DEFAULTS.digest_include_tickets),
          digest_ticket_statuses: get('digest_ticket_statuses', DEFAULTS.digest_ticket_statuses),
          digest_ticket_priorities: get('digest_ticket_priorities', DEFAULTS.digest_ticket_priorities),
          digest_ticket_min_age_days: get('digest_ticket_min_age_days', DEFAULTS.digest_ticket_min_age_days),
          digest_include_invoices: get('digest_include_invoices', DEFAULTS.digest_include_invoices),
          digest_invoice_collection_statuses: get('digest_invoice_collection_statuses', DEFAULTS.digest_invoice_collection_statuses),
          digest_invoice_include_cancelled: get('digest_invoice_include_cancelled', DEFAULTS.digest_invoice_include_cancelled),
          digest_invoice_min_amount: get('digest_invoice_min_amount', DEFAULTS.digest_invoice_min_amount),
          digest_invoice_due_filter: get('digest_invoice_due_filter', DEFAULTS.digest_invoice_due_filter),
          digest_invoice_due_days: get('digest_invoice_due_days', DEFAULTS.digest_invoice_due_days),
          digest_send_to_assignees: get('digest_send_to_assignees', DEFAULTS.digest_send_to_assignees),
          digest_extra_recipients: get('digest_extra_recipients', DEFAULTS.digest_extra_recipients),
          digest_fallback_to_sender_when_invoices_only: get('digest_fallback_to_sender_when_invoices_only', DEFAULTS.digest_fallback_to_sender_when_invoices_only),
        });
      } catch (error) {
        modal.error('Error cargando los avisos de tickets');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const toggleCsvValue = (key, value) => {
    const arr = csvToArray(form[key]);
    const next = arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value];
    setField(key, next.join(','));
  };

  const onMultiSelect = (key, e) => setField(key, Array.from(e.target.selectedOptions, o => o.value).join(','));

  const saveReminder = async (e) => {
    e.preventDefault();
    setSavingReminder(true);
    try {
      await api.put('/settings/bulk', {
        settings: [
          { key: 'tickets_reminder_enabled', value: form.tickets_reminder_enabled, group: 'tickets' },
          { key: 'tickets_reminder_frequency_days', value: form.tickets_reminder_frequency_days, group: 'tickets' },
          { key: 'tickets_reminder_hour', value: form.tickets_reminder_hour, group: 'tickets' },
          { key: 'tickets_reminder_statuses', value: form.tickets_reminder_statuses, group: 'tickets' },
        ],
      });
      modal.success('Recordatorio a los asignados guardado');
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando el recordatorio');
    } finally {
      setSavingReminder(false);
    }
  };

  const saveDigest = async (e) => {
    e.preventDefault();
    setSavingDigest(true);
    try {
      await api.put('/settings/bulk', {
        settings: [
          { key: 'digest_enabled', value: form.digest_enabled, group: 'digest' },
          { key: 'digest_hour', value: form.digest_hour, group: 'digest' },
          { key: 'digest_weekdays', value: form.digest_weekdays, group: 'digest' },
          { key: 'digest_include_tickets', value: form.digest_include_tickets, group: 'digest' },
          { key: 'digest_ticket_statuses', value: form.digest_ticket_statuses, group: 'digest' },
          { key: 'digest_ticket_priorities', value: form.digest_ticket_priorities, group: 'digest' },
          { key: 'digest_ticket_min_age_days', value: form.digest_ticket_min_age_days, group: 'digest' },
          { key: 'digest_include_invoices', value: form.digest_include_invoices, group: 'digest' },
          { key: 'digest_invoice_collection_statuses', value: form.digest_invoice_collection_statuses, group: 'digest' },
          { key: 'digest_invoice_include_cancelled', value: form.digest_invoice_include_cancelled, group: 'digest' },
          { key: 'digest_invoice_min_amount', value: form.digest_invoice_min_amount, group: 'digest' },
          { key: 'digest_invoice_due_filter', value: form.digest_invoice_due_filter, group: 'digest' },
          { key: 'digest_invoice_due_days', value: form.digest_invoice_due_days, group: 'digest' },
          { key: 'digest_send_to_assignees', value: form.digest_send_to_assignees, group: 'digest' },
          { key: 'digest_extra_recipients', value: form.digest_extra_recipients, group: 'digest' },
          { key: 'digest_fallback_to_sender_when_invoices_only', value: form.digest_fallback_to_sender_when_invoices_only, group: 'digest' },
        ],
      });
      modal.success('Resumen diario guardado');
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando el resumen diario');
    } finally {
      setSavingDigest(false);
    }
  };

  const runReminderNow = async () => {
    try {
      const res = await api.post('/admin/tickets-reminders/run-now');
      const sent = (res.data?.results || []).reduce((acc, r) => acc + (r.sent || 0), 0);
      modal.success(`Recordatorios lanzados. Emails enviados: ${sent}`);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error lanzando los recordatorios');
    }
  };

  const runDigestNow = async () => {
    try {
      const res = await api.post('/admin/digest/run-now');
      const sent = (res.data?.results || []).reduce((acc, r) => acc + (r.sent || 0), 0);
      modal.success(`Resumen lanzado. Emails enviados: ${sent}`);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error lanzando el resumen');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>;

  if (!isSuperAdmin) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Avisos de tickets</h1>
            <p className="page-subtitle">Recordatorios automáticos por email.</p>
          </div>
        </div>
        <div className="card">
          <p style={{ color: '#64748b', margin: 0 }}>
            El recordatorio de cada ticket se activa o desactiva dentro del propio ticket (campo «Recordatorio al asignado»). El resto de la configuración de avisos la gestiona el administrador.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Avisos de tickets</h1>
          <p className="page-subtitle">Recordatorios automáticos por email. Usan la configuración SMTP de la empresa.</p>
        </div>
      </div>

      {/* Recordatorio a los asignados */}
      <form onSubmit={saveReminder}>
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 className="card-title" style={{ marginBottom: 8 }}>Recordatorio a los asignados</h2>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 16 }}>
            Envía un email al asignado de cada ticket cuyo estado esté entre los seleccionados y que tenga el recordatorio activado en el propio ticket, con su estado y los días que faltan para la fecha prevista de finalización.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Activado</label>
              <select className="form-input" value={form.tickets_reminder_enabled} onChange={e => setField('tickets_reminder_enabled', e.target.value)}>
                <option value="false">No</option>
                <option value="true">Sí</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Frecuencia (en días)</label>
              <input className="form-input" type="number" min="1" step="1" value={form.tickets_reminder_frequency_days} onChange={e => setField('tickets_reminder_frequency_days', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Hora del envío (0-23)</label>
              <input className="form-input" type="number" min="0" max="23" step="1" value={form.tickets_reminder_hour} onChange={e => setField('tickets_reminder_hour', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Estados de ticket que lanzan recordatorio</label>
            <select className="form-input" multiple size={5} value={csvToArray(form.tickets_reminder_statuses)} onChange={e => onMultiSelect('tickets_reminder_statuses', e)}>
              {TICKET_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-outline" onClick={runReminderNow}>Enviar ahora</button>
            <button type="submit" className="btn btn-primary" disabled={savingReminder}>{savingReminder ? 'Guardando...' : 'Guardar recordatorio'}</button>
          </div>
        </div>
      </form>

      {/* Resumen diario de tickets y facturas */}
      <form onSubmit={saveDigest}>
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 className="card-title" style={{ marginBottom: 8 }}>Resumen diario de tickets y facturas</h2>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 16 }}>
            Email diario con los tickets de los estados seleccionados y las facturas por cobrar.
          </p>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Activado</label>
              <select className="form-input" value={form.digest_enabled} onChange={e => setField('digest_enabled', e.target.value)}>
                <option value="false">No</option>
                <option value="true">Sí</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Hora del envío (0-23)</label>
              <input className="form-input" type="number" min="0" max="23" step="1" value={form.digest_hour} onChange={e => setField('digest_hour', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Días de la semana</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {WEEKDAYS.map(([v, l]) => {
                const checked = csvToArray(form.digest_weekdays).includes(v);
                return (
                  <label key={v} style={checkboxLabelStyle}>
                    <input type="checkbox" checked={checked} onChange={() => toggleCsvValue('digest_weekdays', v)} />
                    {l}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Tickets */}
          <h4 style={subtitleStyle}>Tickets</h4>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Incluir tickets</label>
              <select className="form-input" value={form.digest_include_tickets} onChange={e => setField('digest_include_tickets', e.target.value)}>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Antigüedad mínima (días)</label>
              <input className="form-input" type="number" min="0" step="1" value={form.digest_ticket_min_age_days} onChange={e => setField('digest_ticket_min_age_days', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Estados que entran</label>
            <select className="form-input" multiple size={5} value={csvToArray(form.digest_ticket_statuses)} onChange={e => onMultiSelect('digest_ticket_statuses', e)}>
              {TICKET_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Prioridades que entran</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {TICKET_PRIORITIES.map(([v, l]) => {
                const checked = csvToArray(form.digest_ticket_priorities).includes(v);
                return (
                  <label key={v} style={checkboxLabelStyle}>
                    <input type="checkbox" checked={checked} onChange={() => toggleCsvValue('digest_ticket_priorities', v)} />
                    {l}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Facturas */}
          <h4 style={subtitleStyle}>Facturas</h4>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Incluir facturas</label>
              <select className="form-input" value={form.digest_include_invoices} onChange={e => setField('digest_include_invoices', e.target.value)}>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Incluir anuladas</label>
              <select className="form-input" value={form.digest_invoice_include_cancelled} onChange={e => setField('digest_invoice_include_cancelled', e.target.value)}>
                <option value="false">No</option>
                <option value="true">Sí</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Importe mínimo (€)</label>
              <input className="form-input" type="number" min="0" step="0.01" value={form.digest_invoice_min_amount} onChange={e => setField('digest_invoice_min_amount', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Estados de cobro que entran</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {INVOICE_COLLECTION_STATUSES.map(([v, l]) => {
                const checked = csvToArray(form.digest_invoice_collection_statuses).includes(v);
                return (
                  <label key={v} style={checkboxLabelStyle}>
                    <input type="checkbox" checked={checked} onChange={() => toggleCsvValue('digest_invoice_collection_statuses', v)} />
                    {l}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Vencimientos</label>
              <select className="form-input" value={form.digest_invoice_due_filter} onChange={e => setField('digest_invoice_due_filter', e.target.value)}>
                <option value="all">Todas</option>
                <option value="overdue">Solo vencidas</option>
                <option value="due_in">Vencen en ≤ N días</option>
              </select>
            </div>
            {form.digest_invoice_due_filter === 'due_in' && (
              <div className="form-group">
                <label className="form-label">N (días)</label>
                <input className="form-input" type="number" min="0" step="1" value={form.digest_invoice_due_days} onChange={e => setField('digest_invoice_due_days', e.target.value)} />
              </div>
            )}
          </div>

          {/* Destinatarios */}
          <h4 style={subtitleStyle}>Destinatarios</h4>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">A los usuarios asignados (cada uno sus tickets)</label>
              <select className="form-input" value={form.digest_send_to_assignees} onChange={e => setField('digest_send_to_assignees', e.target.value)}>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Si solo hay facturas, enviar al remitente SMTP</label>
              <select className="form-input" value={form.digest_fallback_to_sender_when_invoices_only} onChange={e => setField('digest_fallback_to_sender_when_invoices_only', e.target.value)}>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Destinatarios extra (CSV de emails que reciben el resumen completo)</label>
            <input className="form-input" type="text" placeholder="usuario@empresa.com, otro@empresa.com" value={form.digest_extra_recipients} onChange={e => setField('digest_extra_recipients', e.target.value)} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-outline" onClick={runDigestNow}>Enviar ahora</button>
            <button type="submit" className="btn btn-primary" disabled={savingDigest}>{savingDigest ? 'Guardando...' : 'Guardar resumen diario'}</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TicketsAvisos;
